import { Injectable } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { Observable } from 'rxjs/Observable'
import { environment } from '../../../environments/environment'

export interface NbSplittest {
  id: number
  title: string
  fitproduct: number
  perfitproduct: string
  start30daystrial: number
  start30daystrialexpired: string
  fitproductexpired: number
  purchases: string
  purchasesubscription: string
  percentageusers: string
  onetimepurchases: string
  twotimepurchases: number
  usersubscription: number
  totalrevenue: string
}

export interface NbSplitSelectList {
  id: number
  name: any
  selected: number
}

export interface NbSplittestResponse {
  success: boolean
  trialgroups: NbSplittest[]
  memberships: NbSplitSelectList[]
}

@Injectable()
export class ReportService {
  private API_ENDPOINT = `${environment.HOST}/adminapi/report/`
  private NODE_API = `${environment.NODE_API_URL}`

  constructor(private http: HttpClient) {}

  public getsplittest(splitgroups: Array<number>): Observable<NbSplittestResponse> {
    return this.http.post(`${this.API_ENDPOINT}iosstoregroup`, { splitgroups: splitgroups }).map((res) => res as NbSplittestResponse)
  }

  // -----------------------------------------------------------------------------------------------------------------
  // Functions to generate the different reports
  // -----------------------------------------------------------------------------------------------------------------
  public reportSonyFinanceGenerate = (from: number, to: number) => {
    return this.http.post(`${this.NODE_API}/sony-finance-generate`, { from, to })
  }

  public reportSonyUsageGenerate = (from: number, to: number) => {
    return this.http.post(`${this.NODE_API}/sony-usage-generate`, { from, to })
  }

  public reportSonyAnalysisGenerate = (from: number, to: number) => {
    return this.http.post(`${this.NODE_API}/sony-analysis-generate-from-logs`, { from, to })
  }

  public reportSonyFinanceGenerateNewCountries = (from: number, to: number) => {
    return this.http.post(`${this.NODE_API}/sony-finance-generate-new-countries`, { from, to })
  }

  public reportSonyUsageGenerateNewCountries = (from: number, to: number) => {
    return this.http.post(`${this.NODE_API}/sony-usage-generate-new-countries`, { from, to })
  }

  public reportSonyAnalysisGenerateNewCountries = (from: number, to: number) => {
    return this.http.post(`${this.NODE_API}/sony-analysis-generate-from-logs-new-countries`, { from, to })
  }

  public reportUmgFinanceGenerate = (from: number, to: number, country: string) => {
    return this.http.post(`${this.NODE_API}/umg-finance-generate`, { from, to, passedInCountry: country })
  }

  public reportUmgUsageGenerate = (from: number, to: number, country: string) => {
    return this.http.post(`${this.NODE_API}/umg-usage-generate`, { from, to, passedInCountry: country })
  }

  public reportUmgFinanceGenerateNewCountries = (from: number, to: number, countries: any) => {
    return this.http.post(`${this.NODE_API}/umg-finance-generate-new-countries`, { from, to, passedInCountries: countries })
  }

  public reportUmgUsageGenerateNewCountries = (from: number, to: number, countries: any) => {
    return this.http.post(`${this.NODE_API}/umg-usage-generate-new-countries`, { from, to, passedInCountries: countries })
  }

  public reportWarnerFinanceGenerate = (from: number, to: number) => {
    return this.http.post(`${this.NODE_API}/warner-summary-generate-v2`, { from, to })
  }

  public reportWarnerUsageGenerate = (from: number, to: number) => {
    return this.http.post(`${this.NODE_API}/warner-delimited-generate`, { from, to })
  }

  public reportWarnerFinanceGenerateNewCountries = (from: number, to: number) => {
    return this.http.post(`${this.NODE_API}/warner-summary-generate-new-countries`, { from, to })
  }

  public reportWarnerUsageGenerateNewCountries = (from: number, to: number) => {
    return this.http.post(`${this.NODE_API}/warner-delimited-generate-new-countries`, { from, to })
  }

  public reportSummaryGenerate = (year: string, month: string) => {
    return this.http.post(`${this.NODE_API}/reports-final`, { year, month })
  }

  public reportBMIGenerate = (from: string, to: string, vendor: any, country: string) => {
    return this.http.post(`${this.NODE_API}/bmi-generate`, { from, to, vendor, country })
  }

  public reportPartnersGenerate = (from: string, to: string) => {
    return this.http.post(`${this.NODE_API}/reports-partners`, { from, to })
  }

  // -----------------------------------------------------------------------------------------------------------------
  // Functions to get the different reports and Url's
  // -----------------------------------------------------------------------------------------------------------------
  public sonyGetReports = () => {
    return this.http.get(`${this.NODE_API}/sony-reports`)
  }

  public sonyGetReportUrl = (key: string) => {
    return this.http.post(`${this.NODE_API}/sony-report`, { key })
  }

  public umgGetReports = () => {
    return this.http.get(`${this.NODE_API}/umg-reports`)
  }

  public umgGetReportUrl = (key: string) => {
    return this.http.post(`${this.NODE_API}/umg-report`, { key })
  }

  public warnerGetReports = () => {
    return this.http.get(`${this.NODE_API}/warner-reports`)
  }

  public warnerGetReportUrl = (key: string) => {
    return this.http.post(`${this.NODE_API}/warner-report`, { key })
  }

  public summaryGetReports = () => {
    return this.http.get(`${this.NODE_API}/summary-reports`)
  }

  public summaryGetReportUrl = (key: string) => {
    return this.http.post(`${this.NODE_API}/summary-report`, { key })
  }

  public bmiGetReports = () => {
    return this.http.get(`${this.NODE_API}/bmi-reports`)
  }

  public bmiGetReportUrl = (key: string) => {
    return this.http.post(`${this.NODE_API}/bmi-report`, { key })
  }

  // -----------------------------------------------------------------------------------------------------------------
  // Functions to handle the receivers
  // -----------------------------------------------------------------------------------------------------------------
  public getReceivers = () => {
    return this.http.get(`${this.NODE_API}/receivers`)
  }

  public createReceiver = (email: string) => {
    return this.http.post(`${this.NODE_API}/receivers`, { email })
  }

  public deleteReceiver = (id: number) => {
    return this.http.delete(`${this.NODE_API}/receivers/${id}`)
  }

  // -----------------------------------------------------------------------------------------------------------------
  // Functions to handle the summary report
  // -----------------------------------------------------------------------------------------------------------------
  public getSummaryData = () => {
    return this.http.get(`${this.NODE_API}/reports-summaries`)
  }

  public sendSonySftp = (from: string, to: string) => {
    return this.http.post(`${this.NODE_API}/sony-send-sftp`, { from, to })
  }

  public sendUmgSftp = (from: string, to: string) => {
    return this.http.post(`${this.NODE_API}/umg-send-sftp`, { from, to })
  }

  public sendWarnerSftp = (from: string, to: string) => {
    return this.http.post(`${this.NODE_API}/warner-send-sftp-v2`, { from, to })
  }

  public sendAscapSftp = (from: string, to: string) => {
    return this.http.post(`${this.NODE_API}/bmi-send-sftp`, { from, to })
  }
}



// WEBPACK FOOTER //
// /srv/dev/FitRadioAdmin2/src/app/@core/data/report.service.ts